import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { Feather, Sparkles, Map as MapIcon, Users, BookOpen, ScrollText } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: Feather, title: "Literary prose", body: "The narrator writes in a novelist's voice — sensory detail, dialogue in character, pacing that reads like a book." },
  { icon: BookOpen, title: "Branching chapters", body: "Your choices genuinely change the story rather than following a locked path. Each saga is one of one." },
  { icon: MapIcon, title: "Illustrated every beat", body: "Automatic scene art on every major moment. Sagas end up as illustrated novels you can read back cover to cover." },
  { icon: Sparkles, title: "Memory Codex", body: "Named characters, threads, milestones and hero state are tracked and fed back to the narrator to help long sagas stay consistent." },
  { icon: Users, title: "Switch narrators mid-book", body: `Change between ${NARRATOR_LIST} in the middle of a chapter while the Codex carries story details forward.` },
  { icon: ScrollText, title: "Community worlds", body: "Publish your setting to a shared library and read what other writers built inside your world." },
];

const FAQ = [
  {
    q: "Why might I look for a NovelAI alternative?",
    a: "Writers usually compare tools by output style, memory controls, workflow, and pricing. TalesTalesTales emphasizes interactive branching, a persistent Memory Codex, and switchable narrators. Both products support fiction workflows; the best fit depends on what you prioritize."
  },
  {
    q: "Can I use it as a pure novel writer?",
    a: "Yes. TalesTalesTales works as an AI novel co-writer — outline a hero and setting, then let the narrator write each chapter in literary prose based on your prompts. Dice can stay off the whole way. Many players use it that way exclusively."
  },
  {
    q: "How is the writing quality?",
    a: "Claude Sonnet 4.5 is the default narrator; Claude Haiku 4.5, GPT-5, and Gemini 2.5 Pro are each one click away and each has a different tonal register. Try a few and keep the one that fits your book — you can even switch between them chapter by chapter."
  },
  {
    q: "Can I export what I write?",
    a: "Every saga saves automatically and stays readable in your account. Community worlds are shareable. Direct-to-EPUB export is on the roadmap; today you can copy chapters out for your own archive."
  },
  {
    q: "Is there an 18+ mode?",
    a: "Yes — opt-in only, 18+ only, gated behind age verification and a paid Mature Realms add-on. Standard sagas remain safe by default."
  },
];

export default function NovelAiAlternative() {
  return (
    <SeoLandingLayout
      testId="seo-novelai-alternative-page"
      path="/novelai-alternative"
      title="NovelAI Alternative | TalesTalesTales"
      description="A NovelAI alternative built around illustrated interactive fiction with persistent memory. Co-write branching AI novels, switch narrators mid-book, keep continuity across long sagas."
      eyebrow="NovelAI Alternative"
      h1="NovelAI Alternative"
      h1Accent="for illustrated branching novels"
      lede="If you're looking for a NovelAI alternative, this page compares workflows in plain language: interactive branching, continuity controls, narrator options, and where each style may fit best."
      body={(
        <>
          <p>
            <strong>Comparison methodology:</strong> this page uses verified TalesTalesTales behavior and publicly documented competitor information available at review time. Third-party features and pricing can change; always verify current details on the vendor&rsquo;s official site.
          </p>
          <p>
            Writers who move between tools often ask for two things: <strong>illustrations that keep
            up with the prose</strong> and <strong>continuity that survives long books</strong>.
            TalesTalesTales was built with both in mind. Every major scene is auto-illustrated by an
            image model tuned for the setting — you end up with a book that has a picture on every
            important page. A Memory Codex tracks named NPCs, threads, milestones
            and inventory changes on each turn and feeds them back to the narrator, which helps
            chapter 40 build on what happened in chapter 3.
          </p>
          <p>
            The narrator writes in <strong>literary prose</strong>, with sensory detail, in-character
            dialogue and novel-length pacing. Claude Sonnet 4.5 is the default; Claude Haiku 4.5,
            GPT-5, and Gemini 2.5 Pro are all available. You can switch narrators mid-book if you
            want a different register for a new arc — the Codex carries story details across the swap.
          </p>
          <p>
            <strong>Branching is central.</strong> The story changes based on your choices
            rather than following a locked path with cosmetic variance. Every saga is one of one. If
            you write a bold hero who talks their way through problems, the book bends toward
            dialogue. If your hero is reckless with a blade, expect body counts.
          </p>
          <p>
            Light RPG elements (dice, skills, equipment) are available but <strong>optional</strong>.
            You can write a pure literary novel with the dice off, or turn them on and get a story
            shaped by the occasional roll.
          </p>
          <p>
            You can publish your custom worlds to a community library and read what other writers
            built inside them. 10 free turns per day on a no-card account so you can try the voice
            before you commit.
          </p>
          <p>
            <strong>Non-affiliation:</strong> TalesTalesTales is independent and is not affiliated with NovelAI or its parent company.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
      comparison={{
        competitor: "NovelAI",
        rows: [
          { feature: "Primary workflow", us: "Interactive branching gameplay where choices drive scene outcomes", them: "Model-assisted writing workflows; see official docs for current modes" },
          { feature: "Scene visuals", us: "Automatic in-line scene illustrations", them: "Image tooling availability varies by product and plan" },
          { feature: "Memory controls", us: "Memory Codex outside the narrator's context window", them: "Vendor-defined memory/lore controls" },
          { feature: "Narrator options", us: NARRATOR_LIST, them: "Vendor-defined model lineup" },
          { feature: "Rules mechanics", us: "Light RPG elements: dice, skills, equipment", them: "No built-in RPG mechanics layer in core writing workflow" },
          { feature: "Entry pricing", us: "10 turns/day, no card required", them: "See official pricing for current free and paid terms" },
          { feature: "Adult-content controls", us: "Opt-in Mature Realms add-on, age-verified", them: "See official policy and subscription docs" },
        ],
      }}
    />
  );
}